import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '../ui/Sidebar';
import { ToastHost } from '../ui/Toast';
import { ImpersonationBanner } from './ImpersonationBanner';
import { ReleaseFlagDeferralNotice } from './ReleaseFlagDeferralNotice';
import { StagingBanner } from './StagingBanner';
import { SidebarProvider } from './SidebarContext';
import { AppHeader, ThemeController } from './ThemeMenu';

/**
 * Layout shell (Sidebar + Outlet).
 *
 * Quick task 260512-uat-batch (2026-05-12):
 * - TopBar removed (F-13). The Search input was dead UI, "+ Log hours" was
 *   duplicated by DashboardPage's inline button, and BellDropdown overlapped
 *   the action-needed badges on sidebar items (Approvals / Team Events).
 *   Every page already owns its own header.
 * - ScrollToTop (F-01): when the route changes, the new page is scrolled to
 *   the top so users don't land mid-page after navigating from the sidebar.
 *
 * Phase 41 (Plan 41-03 Task 2) baseline preserved: SidebarProvider stays
 * mounted because SidebarProjectTree (mounted inside Sidebar) still consumes
 * `useSidebar` for per-node expansion state and mobile open/close.
 */

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // Also reset the scrollable main element in case the page chrome owns
    // its own overflow container.
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.scrollTop = 0;
  }, [pathname]);
  return null;
}

function LayoutContent() {
  // StagingBanner / ImpersonationBanner are in-flow rows at the top of this
  // h-screen column (they used to be `fixed top-0` overlays with a pt-10
  // offset hack, which still covered the top of OTHER fixed overlays like the
  // task slide-over — MKS-144 UAT 2026-08-12). As flow rows they occupy real
  // height, stack when both are visible, and stay pinned because `main` owns
  // the scroll, so no offset is needed anywhere.
  return (
    <div className="h-screen flex flex-col bg-ms-canvas">
      <StagingBanner />
      <ImpersonationBanner />
      <ReleaseFlagDeferralNotice />
      <ScrollToTop />
      <ThemeController />
      <div className="flex-1 flex overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <AppHeader />
          {/* `relative` makes main the containing block for absolutely
              positioned descendants (e.g. `sr-only` labels). Without it they
              resolve against the initial containing block, escape main's
              overflow clip, and grow the document — so once main hits its
              bottom the window scrolls and the whole shell slides up. */}
          <main className="relative flex-1 overflow-y-auto">
            <div className="px-4 py-6 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export function Layout() {
  return (
    <ToastHost>
      <SidebarProvider>
        <LayoutContent />
      </SidebarProvider>
    </ToastHost>
  );
}
